import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { MapPin, Play, Pause, RotateCcw } from 'lucide-react';

const FunJourneyMap = () => {
  const [bikePosition, setBikePosition] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  // SVG path coordinates for the route (Bern -> Ivrea -> Ferrara -> Rome)
  const routePath = "M 150,120 Q 200,150 250,180 T 350,220 Q 400,240 450,280 T 550,350";
  
  const cities = [
    { name: 'Bern 🇨🇭', x: 150, y: 120, position: 0 },
    { name: 'Ivrea 🇮🇹', x: 250, y: 180, position: 25 },
    { name: 'Ferrara 🇮🇹', x: 350, y: 220, position: 50 },
    { name: 'Rome 🇮🇹', x: 550, y: 350, position: 100 }
  ];

  const startAnimation = () => {
    setIsAnimating(true);
    setIsPaused(false);
    setBikePosition(0);
    
    const id = setInterval(() => {
      setBikePosition(prev => {
        if (prev >= 100) {
          clearInterval(id);
          setIsAnimating(false);
          return 100;
        }
        return prev + 0.3;
      });
    }, 30);
    setIntervalId(id);
  };

  const pauseAnimation = () => {
    if (intervalId) {
      clearInterval(intervalId);
      setIsPaused(true);
      setIsAnimating(false);
    }
  };

  const resetAnimation = () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    setBikePosition(0);
    setIsAnimating(false);
    setIsPaused(false);
  };

  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  // Calculate bike position along the SVG path
  const getBikePosition = () => {
    const progress = bikePosition / 100;
    // Approximate positions along the curved path
    const x = 150 + (400 * progress);
    const y = 120 + (230 * progress) + Math.sin(progress * Math.PI) * 30;
    return { x, y };
  };

  const bikePos = getBikePosition();

  return (
    <div className="relative w-full max-w-6xl mx-auto">
      {/* Controls */}
      <div className="flex justify-center items-center gap-4 mb-6">
        <h3 className="text-2xl font-bold text-gray-900">
          🗺️ Live Route Tracker
        </h3>
      </div>
      
      <div className="flex justify-center gap-3 mb-6">
        {!isAnimating && bikePosition === 0 && (
          <button
            onClick={startAnimation}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
          >
            <Play className="h-5 w-5" />
            Start Journey
          </button>
        )}
        
        {isAnimating && (
          <button
            onClick={pauseAnimation}
            className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
          >
            <Pause className="h-5 w-5" />
            Pause
          </button>
        )}
        
        {(isPaused || bikePosition > 0) && (
          <button
            onClick={resetAnimation}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
          >
            <RotateCcw className="h-5 w-5" />
            Reset
          </button>
        )}
        
        {isPaused && (
          <button
            onClick={startAnimation}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
          >
            <Play className="h-5 w-5" />
            Continue
          </button>
        )}
      </div>

      {/* Map Container */}
      <Card className="p-6 bg-gradient-to-br from-blue-100 via-green-50 to-blue-100 border-4 border-blue-300 shadow-2xl">
        <div className="relative w-full" style={{ paddingBottom: '60%' }}>
          <svg 
            viewBox="0 0 700 450" 
            className="absolute inset-0 w-full h-full"
            style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }}
          >
            {/* Map Background with grid */}
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(100,150,255,0.1)" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="700" height="450" fill="url(#grid)" />
            
            {/* Country Labels */}
            <text x="100" y="80" className="text-2xl font-bold fill-blue-700 opacity-40">SWITZERLAND</text>
            <text x="350" y="200" className="text-2xl font-bold fill-green-700 opacity-40">ITALY</text>
            
            {/* Route Path - Dashed line */}
            <path
              d={routePath}
              fill="none"
              stroke="#3B82F6"
              strokeWidth="8"
              strokeDasharray="10,5"
              strokeLinecap="round"
              opacity="0.6"
            />
            
            {/* Completed Route Path */}
            <path
              d={routePath}
              fill="none"
              stroke="#10B981"
              strokeWidth="8"
              strokeDasharray={`${bikePosition * 10},1000`}
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.1s linear' }}
            />

            {/* City Markers */}
            {cities.map((city, index) => {
              const isReached = bikePosition >= city.position;
              return (
                <g key={index}>
                  {/* City Circle */}
                  <circle
                    cx={city.x}
                    cy={city.y}
                    r="15"
                    fill={isReached ? "#10B981" : "#94A3B8"}
                    stroke="white"
                    strokeWidth="3"
                    className={isReached ? "animate-pulse" : ""}
                  />
                  {/* Pin Icon */}
                  <text
                    x={city.x}
                    y={city.y + 5}
                    textAnchor="middle"
                    className="text-sm font-bold fill-white"
                  >
                    📍
                  </text>
                  {/* City Name */}
                  <text
                    x={city.x}
                    y={city.y - 25}
                    textAnchor="middle"
                    className={`text-sm font-bold ${isReached ? 'fill-green-700' : 'fill-gray-600'}`}
                  >
                    {city.name}
                  </text>
                  {/* Checkmark when reached */}
                  {isReached && (
                    <text
                      x={city.x + 20}
                      y={city.y}
                      className="text-xl animate-bounce"
                    >
                      ✅
                    </text>
                  )}
                </g>
              );
            })}

            {/* Animated Motorcycle */}
            <g transform={`translate(${bikePos.x}, ${bikePos.y})`}>
              {/* Motorcycle emoji */}
              <text
                x="0"
                y="0"
                textAnchor="middle"
                className="text-4xl"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                  transform: isAnimating ? 'scale(1.1)' : 'scale(1)',
                  transition: 'transform 0.3s'
                }}
              >
                🏍️
              </text>
              
              {/* Speed lines when moving */}
              {isAnimating && (
                <>
                  <line x1="-20" y1="0" x2="-30" y2="-5" stroke="gray" strokeWidth="2" opacity="0.5" className="animate-pulse" />
                  <line x1="-20" y1="0" x2="-30" y2="5" stroke="gray" strokeWidth="2" opacity="0.5" className="animate-pulse" />
                </>
              )}
              
              {/* Progress Label */}
              {bikePosition > 0 && (
                <text
                  x="0"
                  y="30"
                  textAnchor="middle"
                  className="text-xs font-bold fill-blue-700"
                  style={{ background: 'white' }}
                >
                  {Math.round(bikePosition)}%
                </text>
              )}
            </g>

            {/* Distance markers */}
            <text x="200" y="165" className="text-xs fill-blue-600 font-semibold">285 km</text>
            <text x="300" y="205" className="text-xs fill-blue-600 font-semibold">351 km</text>
            <text x="450" y="290" className="text-xs fill-blue-600 font-semibold">339 km</text>
          </svg>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Journey Progress</span>
            <span className="font-bold text-blue-600">{Math.round(bikePosition)}%</span>
          </div>
          <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300 ease-linear"
              style={{ width: `${bikePosition}%` }}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white rounded-lg p-3 text-center shadow">
            <div className="text-2xl font-bold text-blue-600">~975 km</div>
            <div className="text-xs text-gray-600">Total Distance</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center shadow">
            <div className="text-2xl font-bold text-green-600">4</div>
            <div className="text-xs text-gray-600">Major Stops</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center shadow">
            <div className="text-2xl font-bold text-orange-600">Mar 26</div>
            <div className="text-xs text-gray-600">Start Date</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center shadow">
            <div className="text-2xl font-bold text-purple-600">2026</div>
            <div className="text-xs text-gray-600">Peace Tour</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default FunJourneyMap;
